import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import Modal from '@/components/atoms/Modal'
import Textarea from '@/components/atoms/Textarea'
import {
	feedbackClassNoPresenceSchema,
	feedbackClassSchema,
} from '@/schemas/feedback.schema'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import inputIsValid from '@/utils/helpers/inputIsValid'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import utc from 'dayjs/plugin/utc.js'
import RadioMultiple from '@/components/atoms/RadioGroupMultiple'
import liveClassService from '@/services/liveClassService'
import { modalCreators } from '@/store/ducks/modal'
import { notificationCreators } from '@/store/ducks/notification'
import { modalsRepository } from '@/repositories'

dayjs.extend(utc)
dayjs.locale('pt-br')

interface FeedbackProps {
	disabledOnClose?: boolean
}

type Inputs = {
	rating?: number
	feedback?: string
	tag?: Array<string>
	tagNoPresence?: Array<string>
	atLeastOne?: string
}

const nameRatings: { [key: number]: string } = {
	1: 'Péssima',
	2: 'Ruim',
	3: 'Razoável',
	4: 'Muito Boa',
	5: 'Incrível',
}

const options3OrLessPresence = [
	{
		label: 'Material Ruim',
		value: 'Material Ruim',
	},
	{
		label: 'Problemas Técnicos',
		value: 'Problemas Técnicos',
	},
	{
		label: 'Plataforma Pouco Intuitiva',
		value: 'Plataforma Pouco Intuitiva',
	},
	{
		label: 'Melhorar Dinâmica da Aula',
		value: 'Melhorar Dinâmica da Aula',
	},
	{
		label: 'Pouca Didática do Tutor',
		value: 'Pouca Didática do Tutor',
	},
	{
		label: 'Atraso na Aula',
		value: 'Atraso na Aula',
	},
]

const options4OrMorePresence = [
	{
		label: 'Ótimo Material de Aula',
		value: 'Ótimo Material de Aula',
	},
	{
		label: 'Tutor Incrível',
		value: 'Tutor Incrível',
	},
	{
		label: 'Ótima Plataforma',
		value: 'Ótima Plataforma',
	},
	{
		label: 'Dinâmica Incrível',
		value: 'Dinâmica Incrível',
	},
	{
		label: 'Aula no Horário',
		value: 'Aula no Horário',
	},
]

const optionsNoPresence = [
	{
		label: 'Compromisso Pessoal',
		value: 'Compromisso Pessoal',
	},
	{
		label: 'Problemas Técnicos',
		value: 'Problemas Técnicos',
	},
	{
		label: 'Problemas de Usabilidade',
		value: 'Problemas de Usabilidade',
	},
	{
		label: 'Conflito de Horários',
		value: 'Conflito de Horários',
	},
	{
		label: 'Imprevisto',
		value: 'Imprevisto',
	},
]

const ErrorAlert = ({ errors }: { errors: FieldErrors<Inputs> }) =>
	(() => {
		if (Object.keys(errors).length) {
			return (
				<div className='text-red-500 text-start font-medium text-sm w-full transition-all duration-150'>
					* {errors?.atLeastOne?.message || 'Ocorreu um erro desconhecido'}
				</div>
			)
		}

		return <></>
	})()

export function FeedbackClass({ disabledOnClose }: FeedbackProps) {
	const {
		modal: { modalInfo },
	} = useAppSelector((state) => state)

	const dispatch = useAppDispatch()

	const [feedback, setFeedback] = useState<{
		rating: keyof typeof nameRatings
		feedback?: string
		tag?: Array<string>
		tagNoPresence?: Array<string>
	}>({
		rating: 0,
		feedback: undefined,
		tag: undefined,
		tagNoPresence: undefined,
	})
	const [tags, setTags] = useState<Array<string>>([])
	const [hover, setHover] = useState(0)

	const [happened, setHappened] = useState(true)

	const {
		execute: feedbackLiveClass,
		isLoading,
		isSuccess,
		isError,
	} = liveClassService.feedbackLiveClass()

	const {
		register,
		handleSubmit,
		setValue,
		control,
		reset,
		clearErrors,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(
			happened ? feedbackClassSchema : feedbackClassNoPresenceSchema
		),
	})

	useEffect(() => {
		reset()
		setValue('tag', undefined)
		setValue('tagNoPresence', undefined)

		if (!happened) {
			setFeedback((prevFeedback) => ({
				...prevFeedback,
				rating: 0,
				tagNoPresence: undefined,
				tag: undefined,
			}))
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!happened])

	useEffect(() => {
		if (isSuccess) {
			setHappened(true)
			setFeedback({
				rating: 0,
				feedback: undefined,
				tag: undefined,
				tagNoPresence: undefined,
			})
			dispatch(modalCreators.closeModal())
			reset()
			dispatch(
				notificationCreators.show({
					message: 'Feedback adicionado!',
				})
			)
			dispatch(modalsRepository.util.invalidateTags(['Modals']))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess])

	const onSubmit = (data: Inputs) => {
		let dataResult = {
			aula_id: modalInfo?.id,
			happened: happened,
			feedback: data.feedback,
			rating: data.rating,
			tags: data.tag,
		}

		if (!happened) {
			dataResult = {
				aula_id: modalInfo?.id,
				happened: happened,
				feedback: '',
				rating: 0,
				tags: data.tagNoPresence,
			}
		}

		feedbackLiveClass(dataResult)
	}

	return (
		<Modal
			name='feedbackClassModal'
			disabledOnClose={disabledOnClose}
			className='relative overflow-clip flex flex-col !px-0 !py-0 max-w-[586px] border-[3px] rounded-2xl border-brand-primary dark:bg-zinc-50'
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div
					className='flex-1 overflow-auto py-8 px-6 md:px-8'
					style={{ maxHeight: `calc(100vh - 180px)` }}
					id='sidebar-content'
				>
					<div className='flex flex-col gap-6'>
						<div className='flex flex-col gap-6'>
							<div className='flex flex-col gap-4'>
								<Dialog.Title
									as='h3'
									className='text-2xl md:text-3xl text-[#150D31] font-bold text-start'
								>
									Conte para nós como foi sua experiência na aula:
								</Dialog.Title>
								{modalInfo?.nomeTurma && modalInfo?.data_aula && (
									<div className='flex items-center gap-3 xs:gap-2 bg-[#EBE5FF] rounded-lg py-2 px-4'>
										<Icon
											name='calendar'
											width='20'
											height='20'
											className='min-w-5 mn-h-5'
										/>
										<p className='text-sm md:text-lg text-[#320361] font-medium text-start first-letter:uppercase'>
											{modalInfo?.nomeTurma} • Aula do dia{' '}
											{dayjs(modalInfo?.data_aula)
												.utc()
												.locale('pt-br')
												.format('DD/MM')}
										</p>
									</div>
								)}
							</div>
							<div
								className={twMerge(
									classNames('flex flex-col gap-4', {
										'opacity-50': !happened,
									})
								)}
							>
								<div className='flex flex-col gap-2'>
									<h5 className='text-xl md:text-2xl text-[#150D31] font-bold text-start'>
										Sobre a Aula<span className='text-[#D31B23]'>*</span>
									</h5>
									<p className='text-base text-[#150D31]/80 font-medium text-start'>
										Leve em consideração a didática do professor, dinâmica e
										aprendizados.
									</p>
								</div>
								<div>
									<div className='flex flex-wrap items-center sm:grid xs:grid-cols-3 md:grid-cols-5 gap-2'>
										{[1, 2, 3, 4, 5].map((rating, key) => {
											return (
												<Controller
													control={control}
													name='rating'
													key={key}
													render={() => (
														<button
															type='button'
															onClick={() => {
																const newRating =
																	feedback?.rating === rating ? 0 : rating
																setValue('rating', newRating)
																setValue('tag', undefined)
																setTags([])
																clearErrors()
																setFeedback((prevFeedback) => ({
																	...prevFeedback,
																	rating: newRating,
																}))
															}}
															onMouseEnter={() => setHover(rating)}
															onMouseLeave={() => setHover(0)}
															className={twMerge(
																classNames(
																	'relative w-full max-w-max sm:max-w-full px-6 md:px-4 flex items-center justify-center focus:outline-none py-2 md:min-h-[72px] rounded-xl border border-[#000000]/20 cursor-pointer transition-colors duration-300 group hover:border-brand-primary hover:bg-[#E4DFF5]',
																	{
																		'col-span-3 md:col-span-1': [4, 5].includes(
																			rating
																		),
																		'bg-[#E4DFF5] border-brand-primary':
																			feedback?.rating === rating,
																		'pointer-events-none': !happened,
																	}
																)
															)}
														>
															<img
																src={`/imgs/admin/modals/rating-0${rating}.png`}
																width='40px'
																height='40px'
																alt={`Rating Level ${rating}`}
																className={twMerge(
																	classNames(
																		'w-9 h-9 md:w-10 md:h-10 grayscale group-hover:grayscale-0 transition-all duration-500',
																		{
																			'grayscale-0':
																				feedback?.rating === rating,
																		}
																	)
																)}
															/>

															<div
																className={twMerge(
																	'absolute min-w-max pointer-events-none -top-11 bg-brand-primary py-2 px-4 rounded-lg transition-all duration-500 opacity-0',
																	classNames({
																		'opacity-100 flex': hover === rating,
																	}),
																	'after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[4px] after:border-brand-primary/20'
																)}
															>
																<p className='text-white text-[13px] font-semibold leading-5 text-center'>
																	{nameRatings[rating]}
																</p>
															</div>
														</button>
													)}
												/>
											)
										})}
									</div>
								</div>
							</div>

							{(() => {
								if (happened && feedback?.rating) {
									return (
										<div className='flex flex-col gap-4'>
											{(errors || isError) && <ErrorAlert errors={errors} />}
											<div className='flex flex-col gap-2'>
												<h5 className='text-xl md:text-2xll text-[#150D31] font-bold text-start'>
													Comentário
													<span
														className={twMerge(
															classNames('text-[#D31B23]', {
																hidden: feedback?.rating > 3,
															})
														)}
													>
														*
													</span>
												</h5>
												<p className='text-base text-[#150D31]/80 font-medium text-start'>
													Selecione as opções e deixe um feedback adicional
													sobre como podemos melhorar
												</p>
											</div>

											<div className='flex flex-col'>
												<Controller
													control={control}
													name='tag'
													render={() => (
														<RadioMultiple
															selectedItem={tags}
															setSelectedItem={(item) => {
																const itemString = item.toString()

																const updatedTags = tags.includes(itemString)
																	? tags.filter((a) => a !== itemString)
																	: [...tags, itemString]

																setValue('tag', updatedTags)
																setTags(updatedTags)
															}}
															itens={
																feedback?.rating > 3
																	? options4OrMorePresence
																	: options3OrLessPresence
															}
														/>
													)}
												/>
											</div>
											<div>
												<Textarea
													isValid={inputIsValid(
														errors.feedback,
														touchedFields.feedback
													)}
													{...register('feedback')}
													placeholder='Escreva um comentário...'
													rows={3}
													className='mb-0 rounded-[10px] dark:border font-semibold dark:border-[#000000]/40 dark:text-[#000000] dark:placeholder:text-[#000000]/40 dark:focus:border-brand-primary'
												/>
											</div>
										</div>
									)
								}
								return <></>
							})()}
						</div>
						<hr />
						<div className='flex flex-col gap-6'>
							<div className='flex flex-col gap-6'>
								<label className='relative inline-flex flex-col cursor-pointer'>
									<input
										type='checkbox'
										className='sr-only peer'
										checked={happened}
										onChange={() => {
											setTags([])
											setValue('tagNoPresence', undefined)
											setHappened((toogle) => !toogle)
										}}
									/>

									<div className='flex items-center gap-2'>
										<span
											className={twMerge(
												'flex w-5 h-5 rounded-full bg-transparent border-[2px] border-[#000000]/60 items-center justify-center',
												classNames({
													'border-[2px] border-brand-primary/60 bg-brand-primary':
														!happened,
												})
											)}
										>
											{!happened && (
												<Icon
													name='check'
													width='14px'
													height='14px'
													fill='#F7F9FB'
												/>
											)}
										</span>
										<p className='text-base font-medium leading-[150%] text-start text-[#150D31] select-none'>
											Eu não participei da aula
										</p>
									</div>
								</label>

								{(() => {
									if (!happened) {
										return (
											<div className='flex flex-col gap-3'>
												<p className='text-base text-[#150D31]/80 font-medium text-start'>
													Selecione o(s) motivo(s) que mais se encaixam:
												</p>
												<div className='flex flex-col'>
													<Controller
														control={control}
														name='tagNoPresence'
														render={() => (
															<RadioMultiple
																selectedItem={tags}
																setSelectedItem={(item) => {
																	const itemString = item.toString()

																	const updatedTags = tags.includes(itemString)
																		? tags.filter((a) => a !== itemString)
																		: [...tags, itemString]

																	setValue('tagNoPresence', updatedTags)
																	setTags(updatedTags)
																}}
																itens={optionsNoPresence}
															/>
														)}
													/>
												</div>
											</div>
										)
									}

									return <></>
								})()}
							</div>
						</div>
					</div>
				</div>

				<div
					className={twMerge(
						classNames(
							'w-full flex-shrink-0 flex justify-end items-end px-6 py-5 bg-[#EBE5FF]',
							{ hidden: !feedback?.rating && happened }
						)
					)}
				>
					<Button
						type='submit'
						className='w-full max-w-[196px] rounded-full h-[40px]'
						isLoading={isLoading}
					>
						Enviar feedback
					</Button>
				</div>
			</form>
		</Modal>
	)
}
